import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { MoiDataService } from "@app/moi/services/moi-data.service";
import { Pages } from "@constants/pages/Pages";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { BaseHighchartWidget } from "../BaseHighchartWidget";
import { MoiOverviewModel } from "@models/moi/MoiOverviewModel";
import { nameof } from "@methods/jeffs-toolkit";
import { WidgetFilterKey } from "./../../../shared/models/WidgetFilterKey";
import { firstValueFrom } from "rxjs";
import { provideTranslocoScope } from "@jsverse/transloco";

@Component({
    selector: "moi-widget",
    templateUrl: "../highchart-widget.component.html",
    styleUrls: ["../highchart-widget.component.scss"],
    providers: [provideTranslocoScope("dashboard")]
})
export class MoiWidgetComponent extends BaseHighchartWidget {
    @ViewChild("chartContainer") chartContainer: ElementRef;

    private moiDataService = inject(MoiDataService);
    private pages = inject(Pages);

    constructor() {
        super();
        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.Organization] =
            "organizationID";
        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.BusinessDimension] =
            "businessDimensionIDs";
    }

    protected async getChartConfig() {
        await this.preloadDashboardScope();

        const filters = this.getFilters();
        const data = await firstValueFrom(this.moiDataService.getWidgetInfo(filters));
        const config = this.getBaseChartConfig(null, data.series);
        config.plotOptions.series.stacking = "normal";
        config.xAxis = {
            categories: data.categories,
        };

        config.yAxis = {
            min: 0,
            allowDecimals: false,
            title: {
                text: this.transloco.translate("dashboard.moiWidget.yAxisTitle"),
            },
        };

        return config;
    }

    protected async openWorkspace(ev: Highcharts.PointClickEventObject) {
        const category = ev.point.category;
        const type = ev.point.series.name;

        const searchKeywords: SearchKeyword[] = [
            this.getSearchKeyword(
                nameof<MoiOverviewModel>((x) => x.moiType),
                type
            ),
            ...this.getSearchKeywords(category?.toString()),
        ];

        const preset = await this.getBasePreset(searchKeywords);
        this.openPreset(this.pages.MoiOverview, preset);
    }

    private getSearchKeywords(category: string) {
        const searchKeywords: SearchKeyword[] = [];
        if (category === "Open MoIs") {
            searchKeywords.push(
                this.getSearchKeyword(
                    nameof<MoiOverviewModel>((x) => x.status),
                    "In progress"
                )
            );
            searchKeywords.push(
                this.getSearchKeyword(
                    nameof<MoiOverviewModel>((x) => x.status),
                    "Ready for acceptance"
                )
            );
            searchKeywords.push(
                this.getSearchKeyword(
                    nameof<MoiOverviewModel>((x) => x.status),
                    "Accepted"
                )
            );
        } else if (category === "Closed MoIs") {
            searchKeywords.push(
                this.getSearchKeyword(
                    nameof<MoiOverviewModel>((x) => x.status),
                    "Closed"
                )
            );
        } else if (category === "Overdue MoIs") {
            searchKeywords.push(
                this.getSearchKeyword(
                    nameof<MoiOverviewModel>((x) => x.isOverdue),
                    "Yes"
                )
            );
        }

        return searchKeywords;
    }

    protected async onGlobalOpenClick() {
        const preset = await this.getBasePreset();
        this.openPreset(this.pages.MoiOverview, preset);
    }
}
